import { useNavigate } from 'react-router-dom'
import { datadogRum } from '@datadog/browser-rum'
import { Referrers } from '../../consts/defaultConfigValues/defaultConstants'
import { IPrefillCartMutationResponse } from '../../graphQL/mutations/prefillCart/types'
import { IOrderLine } from '../../graphQL/queries/getOrders/types'
import { ICartProduct } from '../../interfaces'
import { addAlert } from '../../redux/slices/config'
import { generateDataDogContext } from '../../utils/dataDog'
import getAlertErrorMessage, {
  AlertMessageType,
} from '../../utils/getAlertErrorMessage'
import {
  IAddPrefillCartItem,
  useAddPrefillCart,
} from '../prefillCart/useAddPrefillCart'
import useAppDispatch from '../useAppDispatch'
import useAppSelector from '../useAppSelector'

const FUNCTION_NAME = 'useReorder'

interface ReorderArgs {
  orderId?: unknown
  value?: unknown
}

const readOrderId = (args: ReorderArgs): string => {
  const candidates = [args.orderId, args.value]
  const found = candidates.find(
    (candidate) => typeof candidate === 'string' && candidate.length > 0,
  )
  return typeof found === 'string' ? found : ''
}

/**
 * Maps an order line onto the shape `useAddPrefillCart` consumes.
 *
 * The hook reads `item.package?.name` for `selectedPackage` and uses `price`
 * and `name` for the GTM event only, so the null coercions here cost nothing
 * but a less precise analytics payload on an order that came back without
 * them.
 * @internal
 */
export const toPrefillCartItems = (
  lines: IOrderLine[],
): IAddPrefillCartItem[] =>
  lines.map((line) => ({
    sku: line.sku,
    quantity: line.quantity,
    price: line.price ?? 0,
    name: line.name ?? '',
    discounts: 0,
    package: line.package?.name
      ? ({ name: line.package.name } as ICartProduct['package'])
      : null,
  }))

/**
 * "Order again" from an Order History row.
 *
 * Deliberately not a per-item loop over the cart hook. `useAddPrefillCart`
 * already adds a whole list in one `prefillCart` mutation and already owns
 * `onCartResponse`, `setSynchronizingCart`, the GTM event and the DataDog
 * context, so reorder is a call to it.
 *
 * Scoped to the session the shopper is in right now. The mutation carries the
 * `sessionUid` currently in `defaultSlice` and nothing here reads or writes
 * any other session — reorder across a session hop is REQ-02 in Scoping M3,
 * after this.
 */
export const useReorder = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { addPrefillCart } = useAddPrefillCart()
  const storefrontName = useAppSelector((state) => state.defaultSlice.storeName)
  const sessionUid = useAppSelector((state) => state.defaultSlice.sessionId)
  const texts = useAppSelector((state) => state.defaultSlice.config?.texts)
  const replaceCart = useAppSelector(
    (state) => state.defaultSlice.config?.options?.reorderReplacesCart ?? false,
  )
  const orders = useAppSelector((state) => state.ordersSlice.list)

  /**
   * Warns when the backend dropped lines that are no longer purchasable.
   *
   * `prefillCart` returns the resulting cart, never a rejection list, so a
   * dropped line is simply one that never arrives. The shopper still goes to
   * the cart: what did get added is really there, and an alert they can read
   * beats a silent partial reorder.
   */
  const alertOnDroppedItems = (
    requested: IAddPrefillCartItem[],
    data: IPrefillCartMutationResponse,
  ) => {
    const returnedSkus = new Set(
      (data?.prefillCart?.items || []).map((item) => item.sku),
    )
    const dropped = requested.filter((item) => !returnedSkus.has(item.sku))
    if (!dropped.length) return

    const message = getAlertErrorMessage(
      AlertMessageType.ReorderPartial,
      texts,
      dropped.length,
    )
    if (message) dispatch(addAlert({ message }))

    const context = generateDataDogContext({
      title: 'reorder dropped unavailable items',
      extraInfo: {
        function: FUNCTION_NAME,
        requested: requested.length,
        droppedSkus: dropped.map((item) => item.sku),
      },
    })
    datadogRum.startView(context.viewName)
    datadogRum.addAction('reorder_items_dropped', context)
  }

  /**
   * Nothing reached the cart, so `/cart` would be a lie. Stay put and say so.
   */
  const failReorder = (exception: unknown, orderId: string) => {
    const message = getAlertErrorMessage(AlertMessageType.ReorderFailed, texts)
    if (message) dispatch(addAlert({ message }))

    const context = generateDataDogContext({
      title: 'reorder failed',
      extraInfo: { function: FUNCTION_NAME, orderId },
    })
    datadogRum.startView(context.viewName)
    datadogRum.addError(
      exception instanceof Error ? exception : new Error('reorder failed'),
      context,
    )
  }

  const reorderItems = async (args: ReorderArgs = {}) => {
    const orderId = readOrderId(args)
    const order = orders.find((candidate) => candidate.id === orderId)
    const lines = order?.items || []

    // An unknown id or an order with no lines is a no-op. Sending an empty
    // item list would still hit the API and, with replaceCart on, would empty
    // the cart for nothing.
    if (!orderId || !order || !lines.length) return
    if (!storefrontName || !sessionUid) return

    const items = toPrefillCartItems(lines)

    let added: IPrefillCartMutationResponse | null = null
    try {
      added = await addPrefillCart({
        items,
        storefrontName,
        sessionUid,
        replaceCart,
        referrer: Referrers.ORDER_HISTORY_REORDER,
        onCompleted: (data) => alertOnDroppedItems(items, data),
      })
    } catch (exception) {
      // Reachable only for a synchronous throw before the mutation — the GTM
      // event and the reduce over item prices both run first.
      failReorder(exception, orderId)
      return
    }

    // A failed mutation resolves rather than rejecting, because
    // `useAddPrefillCart` registers an `onError` callback and Apollo returns
    // `{ data: undefined, errors }` in that case instead of throwing. So the
    // null return, not the catch above, is what a network or GraphQL failure
    // actually looks like here.
    if (!added) {
      failReorder(null, orderId)
      return
    }

    navigate(`/${storefrontName}/${sessionUid}/cart`)
  }

  return { reorderItems }
}

export default useReorder
